import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { lazy, Suspense } from "react";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminRoute } from "./components/AdminRoute";
import { TeamAdminRoute } from "./components/TeamAdminRoute";
import ConsoleShell from "./components/ConsoleShell";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";

// Marketing pages
const Home = lazy(() => import("./pages/Home"));
const ScienceLLMs = lazy(() => import("./pages/ScienceLLMs"));
const Docs = lazy(() => import("./pages/Docs"));
const Login = lazy(() => import("./pages/Login"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));

// Console pages
const Dashboard = lazy(() => import("./pages/console/Dashboard"));
const ApiKeys = lazy(() => import("./pages/console/ApiKeys"));
const ConsoleLLMs = lazy(() => import("./pages/console/ConsoleLLMs"));
const ScienceAgentMarketplace = lazy(() => import("./pages/console/ScienceAgentMarketplace"));
const ScienceAgentDetail = lazy(() => import("./pages/console/ScienceAgentDetail"));
const ExperimentLogs = lazy(() => import("./pages/console/ExperimentLogs"));
const Settings = lazy(() => import("./pages/console/Settings"));

// Team pages
const TeamDashboard = lazy(() => import("./pages/console/team/TeamDashboard"));
const TeamUsage = lazy(() => import("./pages/console/team/TeamUsage"));
const TeamManagement = lazy(() => import("./pages/console/team/TeamManagement"));
const TeamWalletTransactions = lazy(() => import("./pages/console/team/TeamWalletTransactions"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/console/admin/AdminDashboard"));
const AdminTeams = lazy(() => import("./pages/console/admin/AdminTeams"));
const AdminTeamDetail = lazy(() => import("./pages/console/admin/AdminTeamDetail"));
const AdminProviders = lazy(() => import("./pages/console/admin/AdminProviders"));
const ProviderWizard = lazy(() => import("./pages/console/admin/ProviderWizard"));
const AdminProviderModels = lazy(() => import("./pages/console/admin/AdminProviderModels"));
const AdminScienceLLMs = lazy(() => import("./pages/console/admin/AdminScienceLLMs"));
const AdminUsage = lazy(() => import("./pages/console/admin/AdminUsage"));
const AdminUsageAttempts = lazy(() => import("./pages/console/admin/AdminUsageAttempts"));
const AdminBills = lazy(() => import("./pages/console/admin/AdminBills"));
const AdminWalletTransactions = lazy(() => import("./pages/console/admin/AdminWalletTransactions"));
const AdminAuditLogs = lazy(() => import("./pages/console/admin/AdminAuditLogs"));
const AdminEmailConfig = lazy(() => import("./pages/console/admin/AdminEmailConfig"));
const AdminEmailStatus = lazy(() => import("./pages/console/admin/AdminEmailStatus"));
const AdminInsights = lazy(() => import("./pages/console/admin/AdminInsights"));
const AdminMargin = lazy(() => import("./pages/console/admin/AdminMargin"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

// Per-route Suspense + ErrorBoundary: a pending chunk falls back within its own
// route, and a failed chunk load (network error) degrades to a route-level error
// with retry instead of bubbling to the app-level ErrorBoundary.
function lazyRoute(element: React.ReactNode) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>{element}</Suspense>
    </ErrorBoundary>
  );
}

// Console routes are wrapped in ONE shared ConsoleShell at the Router level (see
// below), so the sidebar + header stay mounted across console navigation. This
// helper only adds the per-route ErrorBoundary + Suspense; the fallback renders
// inside the shell's content region (the shell is the persistent ancestor).
function consoleRoute(element: React.ReactNode) {
  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div className="flex h-full items-center justify-center p-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        }
      >
        {element}
      </Suspense>
    </ErrorBoundary>
  );
}

function Router() {
  const [location] = useLocation();
  const inConsole = location === "/console" || location.startsWith("/console/");

  const routes = (
    <Switch>
      {/* Marketing pages */}
      <Route path="/">{lazyRoute(<Home />)}</Route>
      <Route path="/llms">{lazyRoute(<ScienceLLMs />)}</Route>
      <Route path="/docs">{lazyRoute(<Docs />)}</Route>
      <Route path="/login">{lazyRoute(<Login />)}</Route>
      <Route path="/change-password">{lazyRoute(<ChangePassword />)}</Route>

      {/* Console pages (protected) */}
      <Route path="/console">{consoleRoute(<ProtectedRoute><Dashboard /></ProtectedRoute>)}</Route>
      <Route path="/console/dashboard">{consoleRoute(<ProtectedRoute><Dashboard /></ProtectedRoute>)}</Route>
      <Route path="/console/keys">{consoleRoute(<ProtectedRoute><ApiKeys /></ProtectedRoute>)}</Route>
      <Route path="/console/llms">{consoleRoute(<ProtectedRoute><ConsoleLLMs /></ProtectedRoute>)}</Route>
      <Route path="/console/science-agents/:agentKey">{consoleRoute(<ProtectedRoute><ScienceAgentDetail /></ProtectedRoute>)}</Route>
      <Route path="/console/science-agents">{consoleRoute(<ProtectedRoute><ScienceAgentMarketplace /></ProtectedRoute>)}</Route>
      <Route path="/console/logs">{consoleRoute(<ProtectedRoute><ExperimentLogs /></ProtectedRoute>)}</Route>
      <Route path="/console/settings">{consoleRoute(<ProtectedRoute><Settings /></ProtectedRoute>)}</Route>

      {/* Team admin pages (team_admin or system_admin) */}
      <Route path="/console/team/dashboard">{consoleRoute(<TeamAdminRoute><TeamDashboard /></TeamAdminRoute>)}</Route>
      <Route path="/console/team">{consoleRoute(<TeamAdminRoute><TeamManagement /></TeamAdminRoute>)}</Route>
      <Route path="/console/team/members">{consoleRoute(<TeamAdminRoute><TeamManagement /></TeamAdminRoute>)}</Route>
      <Route path="/console/team/usage">{consoleRoute(<TeamAdminRoute><TeamUsage /></TeamAdminRoute>)}</Route>
      <Route path="/console/team/wallet-transactions">{consoleRoute(<TeamAdminRoute><TeamWalletTransactions /></TeamAdminRoute>)}</Route>

      {/* Admin pages (system_admin only) */}
      <Route path="/console/admin/dashboard">{consoleRoute(<AdminRoute><AdminDashboard /></AdminRoute>)}</Route>
      <Route path="/console/admin">{consoleRoute(<AdminRoute><AdminTeams /></AdminRoute>)}</Route>
      <Route path="/console/admin/teams/:tid">{consoleRoute(<AdminRoute><AdminTeamDetail /></AdminRoute>)}</Route>
      <Route path="/console/admin/teams">{consoleRoute(<AdminRoute><AdminTeams /></AdminRoute>)}</Route>
      <Route path="/console/admin/providers">{consoleRoute(<AdminRoute><AdminProviders /></AdminRoute>)}</Route>
      <Route path="/console/admin/providers/new">{consoleRoute(<AdminRoute><ProviderWizard /></AdminRoute>)}</Route>
      <Route path="/console/admin/providers/:id/models">{consoleRoute(<AdminRoute><AdminProviderModels /></AdminRoute>)}</Route>
      <Route path="/console/admin/science-llms">{consoleRoute(<AdminRoute><AdminScienceLLMs /></AdminRoute>)}</Route>
      <Route path="/console/admin/insights">{consoleRoute(<AdminRoute><AdminInsights /></AdminRoute>)}</Route>
      <Route path="/console/admin/margin">{consoleRoute(<AdminRoute><AdminMargin /></AdminRoute>)}</Route>
      <Route path="/console/admin/usage">{consoleRoute(<AdminRoute><AdminUsage /></AdminRoute>)}</Route>
      <Route path="/console/admin/usage-attempts">{consoleRoute(<AdminRoute><AdminUsageAttempts /></AdminRoute>)}</Route>
      <Route path="/console/admin/bills">{consoleRoute(<AdminRoute><AdminBills /></AdminRoute>)}</Route>
      <Route path="/console/admin/wallet-transactions">{consoleRoute(<AdminRoute><AdminWalletTransactions /></AdminRoute>)}</Route>
      <Route path="/console/admin/audit-logs">{consoleRoute(<AdminRoute><AdminAuditLogs /></AdminRoute>)}</Route>
      <Route path="/console/admin/email-config">{consoleRoute(<AdminRoute><AdminEmailConfig /></AdminRoute>)}</Route>
      <Route path="/console/admin/email-status">{consoleRoute(<AdminRoute><AdminEmailStatus /></AdminRoute>)}</Route>

      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );

  // A single ConsoleShell wraps every console route, so the sidebar + header stay
  // mounted while navigating between console pages. Non-console routes render bare.
  return inConsole ? <ConsoleShell>{routes}</ConsoleShell> : routes;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Router />
            </TooltipProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
